import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity, Dumbbell, ChevronRight, Clock, Moon, ArrowRight, BarChart3, Sparkles
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import BodyMapSVG from '../components/BodyMapSVG';
import { useAuth } from '../context/AuthContext';
import { getMusclesForExercise } from '../utils/exerciseMuscles';
import api from '../services/api';
import MovementBaselineCard from '../components/MovementBaselineCard';
import WellnessIQCard from '../components/WellnessIQCard';
import WellnessTrendSparkline from '../components/WellnessTrendSparkline';
import NikkiDailyReadCard from '../components/NikkiDailyReadCard';
import DailyCheckinCard from '../components/DailyCheckinCard';
import BehaviorCheckinCard from '../components/BehaviorCheckinCard';
import WellnessJourneyCard from '../components/WellnessJourneyCard';
import MobileHomeHero from '../components/mobile/MobileHomeHero';
import LivingHome from '../components/living/LivingHome';
import WellnessAscensionPath from '../components/WellnessAscensionPath';
import { getWorldMode, setWorldMode as persistWorldMode } from '../lib/worldMode';

// Pillar -> deep-dive route. Clicking a ring in the Classic hero jumps here.
const PILLAR_ROUTES = {
  movement:  '/performance',
  recovery:  '/recovery',
  mental:    '/mental',
  nutrition: '/nutrition',
};

const WorldModeToggle = ({ mode, onChange }) => (
  <div className="flex justify-center lg:justify-start" data-testid="world-mode-toggle">
    <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
      <button
        type="button"
        onClick={() => onChange('classic')}
        data-testid="world-mode-classic"
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
          mode === 'classic' ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white'
        }`}
      >
        <BarChart3 className="w-3.5 h-3.5" /> Insights
      </button>
      <button
        type="button"
        onClick={() => onChange('living')}
        data-testid="world-mode-living"
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
          mode === 'living' ? 'bg-primary text-black' : 'text-zinc-400 hover:text-white'
        }`}
      >
        <Sparkles className="w-3.5 h-3.5" /> Journey
      </button>
    </div>
  </div>
);

const SessionFlashCard = ({ session, workout, onClick }) => {
  const allPrimary = new Set();
  const allSecondary = new Set();
  const exercises = workout?.exercises || [];
  exercises.forEach(ex => {
    const muscles = getMusclesForExercise(ex.name);
    muscles.primary.forEach(m => allPrimary.add(m));
    muscles.secondary.forEach(m => allSecondary.add(m));
  });
  allPrimary.forEach(m => allSecondary.delete(m));

  const startedAt = session.started_at ? new Date(session.started_at) : null;
  const completedAt = session.completed_at ? new Date(session.completed_at) : null;
  const durationMin = startedAt && completedAt
    ? Math.max(1, Math.round((completedAt - startedAt) / 60000))
    : (workout?.duration_minutes || null);

  const dayLabel = completedAt
    ? completedAt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : 'Unknown';

  return (
    <button
      type="button"
      onClick={onClick}
      className="hpme-card p-4 text-left group hover:border-primary/50 transition-colors min-w-[220px] flex-shrink-0"
      data-testid={`session-flash-card-${session.id}`}
    >
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">{dayLabel}</span>
        <ArrowRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-primary transition-colors" />
      </div>
      <h4 className="font-secondary font-bold text-sm mb-2 line-clamp-2 min-h-[2.5rem]">{session.workout_name}</h4>
      <div className="flex justify-center mb-2" style={{ height: '110px' }}>
        {exercises.length > 0 ? (
          <BodyMapSVG
            highlightMode="exercise"
            primaryMuscles={[...allPrimary]}
            secondaryMuscles={[...allSecondary]}
            size="sm"
            showLabels={false}
            interactive={false}
            showBack={true}
          />
        ) : (
          <div className="flex items-center justify-center text-zinc-600 text-xs">No exercise data</div>
        )}
      </div>
      <div className="flex items-center gap-3 text-[10px] text-zinc-500">
        {durationMin && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{durationMin} min</span>}
        <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" />{exercises.length || session.exercises?.length || 0} ex</span>
      </div>
    </button>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [workoutsById, setWorkoutsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [movementBaseline, setMovementBaseline] = useState(null);
  const [baselineNeedsRetest, setBaselineNeedsRetest] = useState(false);
  const [baselineNextRetest, setBaselineNextRetest] = useState(null);
  const [worldMode, setWorldMode] = useState(getWorldMode);

  const changeWorldMode = (m) => {
    setWorldMode(m);
    persistWorldMode(m);
  };

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, sessionsRes, workoutsRes, baselineRes] = await Promise.all([
        api.getPerformanceSummary(),
        api.getSessions(),
        api.getWorkouts(),
        api.getCurrentBaseline().catch(() => null),
      ]);
      setSummary(summaryRes.data);
      if (baselineRes && baselineRes.data) {
        setMovementBaseline(baselineRes.data.baseline || null);
        setBaselineNeedsRetest(Boolean(baselineRes.data.needs_retest));
        setBaselineNextRetest(baselineRes.data.next_retest_due || null);
      }

      // Last 7 days of completed sessions, newest first
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recent = (sessionsRes.data || [])
        .filter(s => s.completed_at && new Date(s.completed_at) >= sevenDaysAgo)
        .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
      setRecentSessions(recent);

      const map = {};
      (workoutsRes.data || []).forEach(w => { map[w.id] = w; });
      setWorkoutsById(map);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Activity className="w-12 h-12 text-primary animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  const hasWorkouts = summary?.has_workouts;

  return (
    <DashboardLayout>
      <div className={`px-4 md:px-8 pt-2 md:pt-4 pb-4 md:pb-8 space-y-8 ${worldMode === 'living' ? 'living-skin relative' : ''}`}>
        {/* Living dashboard ambient glow — bridges the hero into the re-skinned
            sections so the whole page reads as one world. */}
        {worldMode === 'living' && (
          <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(204,255,0,0.06), transparent 70%)', filter: 'blur(80px)' }} />
        )}
        {/* Header — wellness-first. Hidden in Living mode (LivingHome has its own greeting row). */}
        {worldMode !== 'living' && (
        <PageHeader
          tag="Wellness Intelligence"
          tagColor="#CCFF00"
          icon={Activity}
          title={`Welcome back, ${user?.name?.split(' ')[0] || 'friend'}`}
          subtitle={hasWorkouts ? "Here's how your body's reading today." : 'A few taps to start — your Wellness IQ unfolds as you use the app.'}
          testIdPrefix="dashboard"
        />
        )}

        {/* World toggle — Classic (numbers/rings) ⇄ Living (visual avatar).
            Two parallel flows; the journey begins here on the dashboard. */}
        <WorldModeToggle mode={worldMode} onChange={changeWorldMode} />

        {worldMode === 'living' ? (
          /* LIVING WORLD: a central avatar that reflects the COMPOSITE Wellness
             IQ as a whole, surrounded by four "living" pillar cards (each driven
             by its own sub-score) with Nikki guiding underneath. */
          <LivingHome />
        ) : (
          /* CLASSIC: the original numbers-first hero — composite + pillar rings
             (desktop) and the two-column IQ + Journey hero (mobile). */
          <>
            <MobileHomeHero />
            <div className="hidden lg:block">
              <WellnessIQCard onPillarClick={(key) => {
                const route = PILLAR_ROUTES[key];
                if (route) navigate(route);
              }} />
            </div>
          </>
        )}

        {/* Nikki's Daily Read — the visible surface of her behaviour engine.
            Explains WHY the IQ is what it is + what she's doing about it. */}
        <NikkiDailyReadCard />

        {/* Wellness Journey — gamified progression. In Living it becomes the
            "Wellness Ascension" stage path centerpiece; Classic keeps the
            compact card (desktop-only; the mobile hero already shows it). */}
        <div className={worldMode === 'living' ? '' : 'hidden lg:block'}>
          {worldMode === 'living' ? <WellnessAscensionPath /> : <WellnessJourneyCard />}
        </div>

        {/* 7-day Wellness IQ trend — same visibility logic as the Journey card. */}
        <div className={worldMode === 'living' ? '' : 'hidden lg:block'}>
          <WellnessTrendSparkline />
        </div>

        {/* Two-column: the TWO daily check-ins, side by side —
            Mind & Body (how you FEEL) + Behaviour (how you FOLLOW THROUGH). */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          <DailyCheckinCard />
          <BehaviorCheckinCard />
        </div>

        {/* Mobility baseline + Recent sessions — Classic only. In the Living
            world these already live inside the Move world (/world/movement). */}
        {worldMode !== 'living' && (
        <>
        <MovementBaselineCard
          baseline={movementBaseline}
          needsRetest={baselineNeedsRetest}
          nextRetestDue={baselineNextRetest}
        />

        {/* Recent sessions — a gentle glance back, never streak-shaming. */}
        <div className="hpme-card p-6 overflow-hidden" data-testid="recent-sessions-section">
          <div className="hpme-card-corner" />
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-secondary font-bold text-xl tracking-tight">RECENT SESSIONS</h2>
              <p className="text-zinc-400 text-sm">
                {recentSessions.length > 0
                  ? `Last 7 days · tap any to open its analysis`
                  : 'Your recent sessions will rest here'}
              </p>
            </div>
            {recentSessions.length > 0 && (
              <Link to="/sessions" className="text-primary text-sm flex items-center gap-1 hover:underline" data-testid="view-all-sessions-link">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {recentSessions.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2 snap-x" data-testid="session-flash-cards">
              {recentSessions.map(session => (
                <SessionFlashCard
                  key={session.id}
                  session={session}
                  workout={workoutsById[session.workout_plan_id]}
                  onClick={() => navigate(`/sessions/${session.id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10" data-testid="no-recent-sessions">
              <Moon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No sessions in the last 7 days — your body is resting.</p>
              <Link to="/workouts" className="inline-flex items-center gap-1.5 mt-3 text-primary text-sm hover:underline" data-testid="browse-workouts-empty">
                See today&apos;s Focus <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
